import * as types from 'constants/ActionTypes';
import { purchase as purchaseApi } from 'js-api-client';
import { camelizeKeys } from 'humps';
import growthBook from 'services/growthBook';
import { selectPurchaseToken, selectPurchase } from 'selectors/purchaseSelectors';
import { selectWhitelabelFeatures } from 'selectors/whitelabelSelectors';
import {
  selectSelectedProductsImmutable,
  selectSelectedProductsByPassengerIndex,
} from 'selectors/productsCartSelectors';
import {
  calculateTotalQuantityByProductCode,
  calculateMaxSelectableQuantity,
} from 'utils/productsCart/calculateStock';
import toProductImageSrc from 'utils/productsCart/toProductImageSrc';
import {
  trackAddProductToCart,
  trackRemoveProductFromCart,
  trackProductCatalogViewed,
} from 'metrics/user-analytics/products';
import { setError } from './index';

// ====== PRODUCT CATALOG ACTIONS ======

/**
 * Request product catalog
 * @returns {Object} Dispatch action
 */
function requestProductCatalog() {
  return { type: types.REQUEST_PRODUCT_CATALOG };
}

/**
 * Receive product catalog with mock data
 * Simulates API response with product catalog including stock information
 * @param {Array} products - Array of products from the catalog
 * @returns {Object} Dispatch action
 */
function receiveProductCatalog(products) {
  return {
    type: types.RECEIVE_PRODUCT_CATALOG,
    products,
    receivedAt: Date.now(),
  };
}

/**
 * Receive product catalog error
 * @param {string} error - Error message
 * @returns {Object} Dispatch action
 */
function receiveProductCatalogError(error) {
  return {
    type: types.RECEIVE_PRODUCT_CATALOG_ERROR,
    error,
  };
}

/**
 * Fetch product catalog from API
 * Retrieves available ancillary products (snacks, beverages, etc.) for the purchase
 *
 * @returns {Function} Thunk action creator
 */
export function fetchProductCatalog() {
  return async (dispatch, getState) => {
    const state = getState();
    const features = selectWhitelabelFeatures(state);
    const productsCartEnabled =
      growthBook.getGrowthBookFeature('products_cart_enabled') ?? features.PRODUCTS_CART_ENABLED;

    // Gate the catalog fetch on the products cart flag (GrowthBook, whitelabel fallback).
    // Return an empty catalog (same shape as the error path) so callers can safely destructure.
    if (!productsCartEnabled) return { outgoing: [], incoming: [] };

    dispatch(requestProductCatalog());

    // Construct the API URL
    const token = selectPurchaseToken(state);

    const purchase = selectPurchase(state);

    return purchaseApi
      .getAncillaries(token)
      .then((data) => {
        // Transform backend snake_case to frontend camelCase
        const camelizedData = camelizeKeys(data);

        /**
         * Normalize the product image (URL, data URI or bare base64) into a ready `src`
         * @param {Object} product - Camelized catalog product
         * @returns {Object} Product with a ready `image`
         */
        const toImageSrc = (product) => ({
          ...product,
          image: toProductImageSrc(product.image, product.imageFormat),
        });
        const catalogData = {
          ...camelizedData,
          outgoing: (camelizedData.outgoing || []).map(toImageSrc),
          incoming: (camelizedData.incoming || []).map(toImageSrc),
        };
        dispatch(receiveProductCatalog(catalogData));

        // Track catalog viewed event
        const outgoingProducts = catalogData.outgoing;
        const incomingProducts = catalogData.incoming;
        const catalogSize = outgoingProducts.length + incomingProducts.length;
        const departureDate = purchase.departs?.departure || '';
        const originTerminalId = purchase.departs?.fragments?.[0]?.origin?.id || '';
        trackProductCatalogViewed(originTerminalId, catalogSize, departureDate);

        return catalogData;
      })
      .catch((error) => {
        console.error('Error fetching product catalog:', error);
        dispatch(receiveProductCatalogError(error.message));
        dispatch(setError(300, 'error_loading_products', 'warning', false));
        return { outgoing: [], incoming: [] };
      });
  };
}

// ====== PRODUCT CART ACTIONS ======

/**
 * Add product to cart for a specific passenger
 * Validates stock availability considering all passengers' selections, and the per-item
 * limit configured for the whitelabel, before adding
 * @param {Object} payload - Action payload
 * @param {Object} payload.product - Product object with code, name, price, etc.
 * @param {number} payload.quantity - Quantity to add
 * @param {number} payload.passengerIndex - Index of the passenger
 * @param {string} payload.segment - Journey segment: 'outgoing' or 'incoming'
 * @returns {Function} Thunk action creator
 */
export function addProductToCart({ product, quantity, passengerIndex, segment }) {
  return (dispatch, getState) => {
    const state = getState();
    const selectedProducts = selectSelectedProductsImmutable(state);

    // Calculate total quantity selected by all passengers for this product
    const totalQuantitySelected = calculateTotalQuantityByProductCode(
      selectedProducts,
      segment,
      product.code,
    );

    // Quantity this passenger already holds, excluded from the other passengers' share
    const currentQuantity =
      selectSelectedProductsByPassengerIndex(
        passengerIndex,
        segment,
      )(state).find((p) => p.code === product.code)?.quantity || 0;

    // Max this passenger may hold: the lesser of the stock left and the per-item limit
    const stock = product.stock || 0;
    const maxSelectable = calculateMaxSelectableQuantity({
      stock,
      totalSelected: totalQuantitySelected,
      currentQuantity,
      maxPerItem: selectWhitelabelFeatures(state).PRODUCTS_MAX_QUANTITY_PER_ITEM,
    });

    // `quantity` is a delta here, so the resulting total is what must respect the max.
    // This action only ever grows the selection; reductions go through updateProductQuantity.
    const nextQuantity = currentQuantity + quantity;

    // If out of stock, or the resulting quantity exceeds stock or the limit, don't add it
    if (stock === 0 || nextQuantity > maxSelectable) {
      return;
    }

    // Product already comes in camelCase from catalog (transformed by camelizeKeys)
    const { code, name, price, image, imageFormat, description } = product;
    dispatch({
      type: types.ADD_PRODUCT_TO_CART,
      product: { code, name, price, stock, image, imageFormat, description },
      quantity,
      passengerIndex,
      segment,
    });

    // Track add product event
    const purchase = selectPurchase(state);
    trackAddProductToCart(product, quantity, purchase);
  };
}

/**
 * Remove product from cart for a specific passenger.
 * Tracks the removal event in Mixpanel with product and purchase context.
 *
 * @param {Object} payload - Action payload
 * @param {string} payload.productCode - Product code identifier
 * @param {number} payload.passengerIndex - Index of the passenger
 * @param {string} payload.segment - Journey segment: 'outgoing' or 'incoming'
 * @returns {Function} Thunk action creator
 */
export function removeProductFromCart({ productCode, passengerIndex, segment }) {
  return (dispatch, getState) => {
    const state = getState();

    // Get the product data before removing it for tracking purposes
    const selectedProducts = selectSelectedProductsByPassengerIndex(passengerIndex, segment)(state);
    const product = selectedProducts.find((p) => p.code === productCode);

    dispatch({
      type: types.REMOVE_PRODUCT_FROM_CART,
      productCode,
      passengerIndex,
      segment,
    });

    // Track remove product event
    if (product) {
      const purchase = selectPurchase(state);
      trackRemoveProductFromCart(product, purchase);
    }
  };
}

/**
 * Update product quantity in cart for a specific passenger
 * @param {Object} payload - Action payload
 * @param {string} payload.productCode - Code of the product to update
 * @param {number} payload.quantity - New quantity
 * @param {number} payload.passengerIndex - Index of the passenger
 * @param {string} payload.segment - Journey segment: 'outgoing' or 'incoming'
 * @returns {Function} Thunk action creator
 */
export function updateProductQuantity({ productCode, quantity, passengerIndex, segment }) {
  return (dispatch, getState) => {
    if (quantity === 0) {
      dispatch(removeProductFromCart({ productCode, passengerIndex, segment }));
      return;
    }

    const state = getState();

    // Resolve stock and this passenger's current quantity from the stored selection
    const passengerProducts = selectSelectedProductsByPassengerIndex(
      passengerIndex,
      segment,
    )(state);
    const currentProduct = passengerProducts.find((p) => p.code === productCode);
    const stock = currentProduct?.stock || 0;
    const currentQuantity = currentProduct?.quantity || 0;

    // Total quantity selected for this product across all passengers (includes this passenger)
    const selectedProducts = selectSelectedProductsImmutable(state);
    const totalQuantitySelected = calculateTotalQuantityByProductCode(
      selectedProducts,
      segment,
      productCode,
    );

    // Max this passenger may hold: the lesser of the stock left and the per-item limit
    const maxSelectable = calculateMaxSelectableQuantity({
      stock,
      totalSelected: totalQuantitySelected,
      currentQuantity,
      maxPerItem: selectWhitelabelFeatures(state).PRODUCTS_MAX_QUANTITY_PER_ITEM,
    });

    // `quantity` is the resulting total here, unlike addProductToCart which receives a delta.
    // Naming it the same way keeps both guards comparing the same magnitude.
    const nextQuantity = quantity;

    // Only growth is restricted. Both conditions live inside that branch so a reduction is never
    // blocked: a passenger must always be able to lower what is already in the cart, including a
    // hydrated quantity above the limit and a product that sold out after being added.
    if (nextQuantity > currentQuantity && (stock === 0 || nextQuantity > maxSelectable)) {
      return;
    }

    dispatch({
      type: types.UPDATE_PRODUCT_QUANTITY,
      productCode,
      quantity,
      passengerIndex,
      segment,
    });
  };
}

/**
 * Handle product unavailability error
 * Removes unavailable products from cart, updates catalog stock to 0, and sets error flags
 *
 * @param {Object} payload - Action payload
 * @param {Array<string>} payload.productCodes - Array of product codes that are unavailable
 * @returns {Object} Dispatch action
 */
export function handleProductUnavailable({ productCodes }) {
  return {
    type: types.HANDLE_PRODUCT_UNAVAILABLE,
    productCodes,
  };
}
